import React, { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ownerApi, inr, monthLabel } from "@/lib/ownerApi";

const SEL = "h-9 rounded-md border border-stone-300 bg-white px-2 text-sm w-full";

const AddForm = ({ meta, onSaved, prefill }) => {
  const blank = { month: meta.months[0], category: "Rent", amount: "", kind: "fixed", method: "bank", date: "", paid_to: "", note: "" };
  const [v, setV] = useState(blank);
  useEffect(() => { if (prefill) setV((x) => ({ ...x, ...prefill })); }, [prefill]);
  const save = async () => {
    try { await ownerApi.post("/fin/expenses", { ...v, amount: Number(v.amount) }); toast.success("Expense recorded"); setV(blank); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const f = (k) => ({ value: v[k], onChange: (e) => setV({ ...v, [k]: e.target.value }) });
  return (
    <div className="rounded-xl border bg-white p-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 items-end" data-testid="expense-form">
      <select className={SEL} {...f("month")} data-testid="expense-month">{meta.months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}</select>
      <select className={SEL} {...f("category")} data-testid="expense-category">{meta.categories.map((c) => <option key={c}>{c}</option>)}</select>
      <Input type="number" placeholder="Amount ₹" {...f("amount")} data-testid="expense-amount" />
      <select className={SEL} {...f("kind")} data-testid="expense-kind">{meta.kinds.map((c) => <option key={c}>{c}</option>)}</select>
      <select className={SEL} {...f("method")} data-testid="expense-method"><option value="bank">bank</option><option value="cash">cash</option></select>
      <Input type="date" {...f("date")} className="col-span-2 md:col-span-1" data-testid="expense-date" />
      <Input placeholder="Paid to / note" {...f("note")} className="col-span-2 md:col-span-1" data-testid="expense-note" />
      <Button onClick={save} disabled={!v.amount} className="bg-emerald-800 hover:bg-emerald-900 col-span-2 md:col-span-1" data-testid="expense-save">Record actual</Button>
    </div>
  );
};

export default function Expenses() {
  const [d, setD] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const load = useCallback(async () => { const r = await ownerApi.get("/fin/expenses"); setD(r.data); }, []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loader2 className="h-5 w-5 animate-spin text-stone-500" />;
  const recorded = new Set(d.actual.filter((a) => !a.void).map((a) => `${a.category}|${a.month}`));
  const voidIt = async (id) => {
    const reason = window.prompt("Why cancel this expense? (kept in history)");
    if (!reason) return;
    try { await ownerApi.post(`/fin/expenses/${id}/void`, { reason }); load(); } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
  };
  return (
    <div className="space-y-5" data-testid="owner-expenses">
      <p className="text-sm text-stone-600">Record every rupee actually paid. Sheet1 Rent, Electricity and Others are <b>estimates</b> until you record the actual amount here. Teacher salaries are tracked separately in Monthly Salary.</p>
      <AddForm meta={d} onSaved={load} prefill={prefill} />
      <section className="space-y-2">
        <h4 className="font-semibold text-stone-800">Estimates from Sheet1 (tap to record actual)</h4>
        <div className="flex flex-wrap gap-1.5" data-testid="expense-estimates">
          {d.estimates.map((e) => {
            const done = recorded.has(`${e.category}|${e.month}`);
            return <button key={`${e.category}${e.month}`} disabled={done} onClick={() => setPrefill({ month: e.month, category: e.category, amount: String(e.amount), kind: "fixed" })}
              className={`rounded-full border px-2.5 py-1 text-xs ${done ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-900 hover:bg-amber-100"}`} data-testid={`estimate-${e.category}-${e.month}`}>
              {monthLabel(e.month)} {e.category} {inr(e.amount)} {done ? "✓ actual" : "· estimate"}</button>;
          })}
        </div>
      </section>
      <section className="space-y-2">
        <h4 className="font-semibold text-stone-800">Actual expenses</h4>
        <div className="overflow-x-auto rounded-xl border bg-white">
          <table className="w-full text-sm" data-testid="expense-list">
            <thead className="bg-stone-50 text-[11px] uppercase text-stone-500"><tr>{["Month", "Category", "Type", "Amount", "Method", "Date", "Note", ""].map((h) => <th key={h} className="px-3 py-2 text-left">{h}</th>)}</tr></thead>
            <tbody>
              {d.actual.length === 0 && <tr><td colSpan={8} className="px-3 py-3 text-stone-500">No actual expenses recorded yet.</td></tr>}
              {d.actual.map((a) => (
                <tr key={a.id} className={`border-t ${a.void ? "line-through text-stone-400" : ""}`} data-testid={`expense-row-${a.id}`}>
                  <td className="px-3 py-1.5">{monthLabel(a.month)}</td><td className="px-3">{a.category}</td><td className="px-3">{a.kind}</td>
                  <td className="px-3 font-semibold">{inr(a.amount)}</td><td className="px-3">{a.method}</td><td className="px-3">{a.date || "—"}</td>
                  <td className="px-3 text-xs">{a.note}{a.void ? ` (cancelled: ${a.void_reason})` : ""}</td>
                  <td className="px-3">{!a.void && <button className="text-xs text-red-700 underline" onClick={() => voidIt(a.id)} data-testid={`expense-void-${a.id}`}>Cancel</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
